/** The tool strip above the canvas: add elements, group/ungroup/mask, align and distribute, view toggles. */
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  Circle,
  Group,
  Layers2,
  Minus,
  RectangleHorizontal,
  Rows3,
  Scan,
  Square,
  SquareDashed,
  Type,
  Ungroup,
} from 'lucide-react';
import { t } from '../i18n.ts';
import { addElement, alignSelection, distributeSelection, groupSelection, ungroupSelection } from '../state/actions.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { IconButton } from '../ui/controls.tsx';
import { centreCut } from '../model/snapping.ts';

export function Toolbar({ store }: { store: EditorStore }) {
  const selection = useEditor(store, (s) => s.selection);
  const ui = useEditor(store, (s) => s.ui);
  const canvas = useEditor(store, (s) => sceneView(s).canvas);
  const hasCut = centreCut(canvas.width, canvas.height) !== undefined;
  const n = selection.length;
  return (
    <div className="toolbar" role="toolbar" aria-label={t('toolbar.label')}>
      <div className="tool-group" aria-label={t('toolbar.add')}>
        <button type="button" className="tool" onClick={() => addElement(store, 'text')} data-testid="add-text" title={`${t('tool.text')} (T)`}>
          <Type size={15} aria-hidden /> {t('tool.text')}
        </button>
        <button type="button" className="tool" onClick={() => addElement(store, 'box')} data-testid="add-box" title={`${t('tool.boxHint')} (B)`}>
          <RectangleHorizontal size={15} aria-hidden /> {t('tool.box')}
        </button>
        <button type="button" className="tool" onClick={() => addElement(store, 'stack')} data-testid="add-stack" title={`${t('tool.stackHint')} (K)`}>
          <Rows3 size={15} aria-hidden /> {t('tool.stack')}
        </button>
        <IconButton label={`${t('tool.rect')} (R)`} onClick={() => addElement(store, 'rect')} testId="add-rect">
          <Square size={15} />
        </IconButton>
        <IconButton label={`${t('tool.ellipse')} (O)`} onClick={() => addElement(store, 'ellipse')} testId="add-ellipse">
          <Circle size={15} />
        </IconButton>
        <IconButton label={`${t('tool.line')} (L)`} onClick={() => addElement(store, 'line')} testId="add-line">
          <Minus size={15} />
        </IconButton>
      </div>
      <div className="tool-group">
        <IconButton label={t('tool.group')} onClick={() => groupSelection(store)} disabled={n === 0} testId="group">
          <Group size={15} />
        </IconButton>
        <IconButton label={t('tool.ungroup')} onClick={() => ungroupSelection(store)} disabled={n === 0} testId="ungroup">
          <Ungroup size={15} />
        </IconButton>
        <IconButton label={t('tool.mask')} onClick={() => groupSelection(store, 'clipMask')} disabled={n === 0} testId="mask">
          <SquareDashed size={15} />
        </IconButton>
      </div>
      <div className="tool-group" aria-label={t('toolbar.align')}>
        <IconButton label={t('align.left')} onClick={() => alignSelection(store, 'left')} disabled={n === 0}>
          <AlignStartVertical size={15} />
        </IconButton>
        <IconButton label={t('align.hcenter')} onClick={() => alignSelection(store, 'hcenter')} disabled={n === 0} testId="align-hcenter">
          <AlignCenterVertical size={15} />
        </IconButton>
        <IconButton label={t('align.right')} onClick={() => alignSelection(store, 'right')} disabled={n === 0}>
          <AlignEndVertical size={15} />
        </IconButton>
        <IconButton label={t('align.top')} onClick={() => alignSelection(store, 'top')} disabled={n === 0}>
          <AlignStartHorizontal size={15} />
        </IconButton>
        <IconButton label={t('align.vmiddle')} onClick={() => alignSelection(store, 'vmiddle')} disabled={n === 0}>
          <AlignCenterHorizontal size={15} />
        </IconButton>
        <IconButton label={t('align.bottom')} onClick={() => alignSelection(store, 'bottom')} disabled={n === 0}>
          <AlignEndHorizontal size={15} />
        </IconButton>
        <IconButton label={t('align.distributeH')} onClick={() => distributeSelection(store, 'x')} disabled={n < 3}>
          <AlignHorizontalDistributeCenter size={15} />
        </IconButton>
        <IconButton label={t('align.distributeV')} onClick={() => distributeSelection(store, 'y')} disabled={n < 3}>
          <AlignVerticalDistributeCenter size={15} />
        </IconButton>
      </div>
      <div className="spacer" />
      <div className="tool-group" aria-label={t('toolbar.view')}>
        <IconButton label={t('view.safe')} pressed={ui.showSafe} onClick={() => store.setUi({ showSafe: !ui.showSafe })} testId="toggle-safe">
          <Scan size={15} />
        </IconButton>
        {hasCut && (
          <button type="button" className={`tool small${ui.showCut ? ' pressed' : ''}`} aria-pressed={ui.showCut} onClick={() => store.setUi({ showCut: !ui.showCut })} title={t('view.cut')} data-testid="toggle-cut">
            4:3
          </button>
        )}
        <IconButton label={t('view.design')} pressed={ui.renderState === 'design'} onClick={() => store.setUi({ renderState: ui.renderState === 'design' ? 'hold' : 'design' })}>
          <Layers2 size={15} />
        </IconButton>
      </div>
    </div>
  );
}
